import { WorkspaceMember } from '@/types/workspace'
import { User } from '../types/user'
import { CSRF_HEADER_NAME, vpApi } from './vpApi'

const getCsrfToken = async () => {
  const response = await vpApi.get('/v1/accounts/csrf/')
  return response.headers[CSRF_HEADER_NAME] as string
}

type VerifyEmailPayload = {
  uid: string
  token: string
}

const sendVerifyEmail = (payload: VerifyEmailPayload) => {
  return vpApi.post('/v1/accounts/email-verification/', payload)
}

type ValidateTokenPayload = {
  uid: string
  token: string
}

type ValidateTokenResponse = {
  email: string
  verified: boolean
}

// validate password reset token or email verification token
const validateToken = (payload: ValidateTokenPayload) => {
  return vpApi.post<ValidateTokenResponse>(
    '/v1/accounts/token-validation/',
    payload
  )
}

type RegistrationPayload = {
  email: string
  first_name: string
  last_name: string
  password: string
  token: string
}

const completeRegistration = (payload: RegistrationPayload) => {
  return vpApi.post('/v1/accounts/registration/', payload)
}

type LoginPayload = {
  email: string
  password: string
}

type LoginResponse = {
  mfa_required: boolean
  uid: string
  resend_key?: string
}

const login = async (payload: LoginPayload) => {
  const res = await vpApi.post<LoginResponse>('/v1/accounts/login/', payload)
  return res.data
}

type SsoLoginPayload = {
  provider: 'google' | 'microsoft'
  payload: {
    code: string
    token?: string // invite token, required for sign-up
  }
}

const ssoLogin = ({ provider, payload }: SsoLoginPayload) => {
  return vpApi.post(`/v1/accounts/sso/${provider}/`, payload)
}

type GetRedirectUrlResponse = {
  google: string
  microsoft: string
}

const getRedirectUrl = async () => {
  const res = await vpApi.get<GetRedirectUrlResponse>(
    '/v1/accounts/sso-redirect-urls/'
  )
  return res.data
}

type VerifyOtpPayload = {
  uid: string
  otp: string
}

const verifyOtp = (payload: VerifyOtpPayload) => {
  return vpApi.post('/v1/accounts/verify-otp/', payload)
}

type ResendOtpPayload = {
  uid: string
  resend_key: string
}

const resendOtp = (payload: ResendOtpPayload) => {
  return vpApi.post('/v1/accounts/send-otp/', payload)
}

type WhoAmIResponse = {
  user: User & {
    workspace_permissions: string[]
  }
}

const whoAmI = async () => {
  const res = await vpApi.get<WhoAmIResponse>('/v1/accounts/whoami/')
  return res.data
}

const logout = () => {
  return vpApi.delete('/v1/accounts/logout/')
}

// Use 'email' to send the first password reset email.
// Use 'uid' to resend the password reset email.
type PasswordResetPayload =
  | {
      email: string
    }
  | {
      uid: string
    }

const sendPasswordResetEmail = (payload: PasswordResetPayload) => {
  return vpApi.post('/v1/accounts/reset-password/', payload)
}

type ResetPasswordConfirmParams = {
  uid: string
  password: string
  token: string
}

const resetPasswordConfirm = ({
  uid,
  password,
  token
}: ResetPasswordConfirmParams) => {
  return vpApi.post(`/v1/accounts/${uid}/reset-password/confirm/`, {
    password,
    token
  })
}

const getMyWorkspaceMemberships = async () => {
  const res = await vpApi.get<WorkspaceMember[]>('/v1/accounts/my-workspaces/')
  return res.data
}

const changePassword = async (password: string, currentPassword: string) => {
  await vpApi.patch('/v1/accounts/change-password/', {
    password,
    current_password: currentPassword
  })
}

export const accountApi = {
  getCsrfToken,
  sendVerifyEmail,
  validateToken,
  completeRegistration,
  login,
  ssoLogin,
  getRedirectUrl,
  verifyOtp,
  resendOtp,
  whoAmI,
  logout,
  sendPasswordResetEmail,
  resetPasswordConfirm,
  getMyWorkspaceMemberships,
  changePassword
}
